import React, { useState, useEffect } from 'react'
import { Send, X, Camera, CheckCircle, Shield } from 'lucide-react'
import { useBalance } from '../contexts/BalanceContext'
import QRScanner from './QRScanner'
import Toast from './Toast'
import api from '../services/api'
import googlePayLogo from '../assets/googlepay.png'
import phonePeLogo from '../assets/phonepe.png'
import paytmLogo from '../assets/paytm.png'
import bhimLogo from '../assets/bhim.jpg'
import { Button } from './ui/Button'

const STEPS = {
  FORM: 'form',
  APP_CHOOSER: 'app_chooser',
  CONFIRMATION: 'confirmation',
  SUMMARY: 'summary'
}

function QuickPayment({ isOpen, onClose, preSelectedCategory }) {
  const {
    categories,
    getCategoryById,
    loadData
  } = useBalance()

  const [step, setStep] = useState(STEPS.FORM)
  const [isProcessing, setIsProcessing] = useState(false)
  const [toast, setToast] = useState(null)
  const [errors, setErrors] = useState({})
  const [showScanner, setShowScanner] = useState(false)
  const [paymentSummary, setPaymentSummary] = useState({})

  const [formData, setFormData] = useState({
    amount: '',
    categoryId: '',
    upiId: '',
    merchantName: ''
  })

  const defaultCategories = [
    { id: 'default-food', name: 'Food', current_balance: 5000, color: '#EF4444' },
    { id: 'default-rent', name: 'Rent', current_balance: 15000, color: '#3B82F6' }
  ]

  const availableCategories =
    categories?.length > 0 ? categories : defaultCategories

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const initialCategory = preSelectedCategory || availableCategories[0]

    setFormData({
      amount: '',
      categoryId: initialCategory?.id || '',
      upiId: '',
      merchantName: ''
    })

    setErrors({})
    setStep(STEPS.FORM)
    setIsProcessing(false)
    sessionStorage.removeItem('pendingPayment')
  }, [isOpen, availableCategories, preSelectedCategory])

  const showToastMessage = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  const validateForm = () => {
    const errs = {}

    if (!formData.amount || Number(formData.amount) <= 0)
      errs.amount = 'Enter valid amount'

    if (!formData.categoryId)
      errs.categoryId = 'Select category'

    if (!/^[\w.-]+@[\w.-]+$/.test(formData.upiId))
      errs.upiId = 'Invalid UPI ID'

    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  /* ================= COPY & CONTINUE ================= */
  const handlePayment = async () => {
    if (!validateForm()) return

    try {
      setIsProcessing(true)

      const clipText = `₹${formData.amount} to ${formData.upiId}`
      await navigator.clipboard.writeText(clipText)

      sessionStorage.setItem(
        'pendingPayment',
        JSON.stringify({ ...formData, ts: Date.now() })
      )

      showToastMessage('Amount & UPI ID copied!')
      setStep(STEPS.APP_CHOOSER)
    } catch {
      showToastMessage('Unable to copy details', 'error')
    } finally {
      setIsProcessing(false)
    }
  }

  /* ================= OPEN UPI APP ================= */
  const openUPIApp = (appType) => {
    const upiApps = {
      gpay: 'tez://upi/',
      phonepe: 'phonepe://',
      paytm: 'paytmmp://',
      bhim: 'bhim://'
    }

    if (appType === 'other') {
      showToastMessage('Open your UPI app and paste the details', 'info')
      setTimeout(() => setStep(STEPS.CONFIRMATION), 600)
      return
    }

    const deepLink = upiApps[appType]
    if (deepLink) window.location.href = deepLink

    setTimeout(() => setStep(STEPS.CONFIRMATION), 800)
  }

  /* ================= CONFIRMATION ================= */
  const handlePaymentConfirmation = async (success) => {
    if (!success) {
      sessionStorage.removeItem('pendingPayment')
      showToastMessage('Payment cancelled', 'info')
      setStep(STEPS.FORM)
      return
    }

    try {
      setIsProcessing(true)

      const category = getCategoryById(formData.categoryId)
      const amount = Number(formData.amount)
      const prevBalance = category.current_balance

      const txn = await api.post('/transactions', {
        category_id: formData.categoryId,
        amount,
        merchant_name: formData.merchantName || formData.upiId,
        merchant_upi: formData.upiId,
        note: 'UPI payment (manual)'
      })

      await api.put(`/transactions/${txn.data.transaction.id}/status`, {
        status: 'success'
      })

      await loadData()

      setPaymentSummary({
        previousBalance: prevBalance,
        amountSpent: amount,
        newBalance: prevBalance - amount,
        categoryName: category.name
      })

      sessionStorage.removeItem('pendingPayment')
      setStep(STEPS.SUMMARY)
      showToastMessage('Payment recorded')
    } catch {
      showToastMessage('Failed to save transaction', 'error')
    } finally {
      setIsProcessing(false)
    }
  }

  /* ================= QR SCAN ================= */
  const handleQRScan = (data) => {
    if (data.upiId) {
      setFormData(prev => ({
        ...prev,
        upiId: data.upiId,
        merchantName: data.merchantName || prev.merchantName
      }))
      showToastMessage('UPI ID scanned')
    }
    setShowScanner(false)
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="glass-card w-full max-w-md p-0 overflow-hidden border border-white/10 shadow-2xl shadow-primary-500/10">

          {/* HEADER */}
          <div className="flex justify-between items-center p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="bg-primary-500/20 p-2 rounded-lg">
                <Send className="h-5 w-5 text-primary-400" />
              </div>
              <h3 className="font-bold text-lg text-white">Quick Payment</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white/60 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* ================= FORM ================= */}
          {step === STEPS.FORM && (
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-medium text-white/60 mb-1.5 ml-1">Payment Amount</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">₹</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={e => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white rounded-xl pl-8 pr-4 py-3 placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all font-semibold text-lg"
                  />
                </div>
                {errors.amount && <p className="text-red-400 text-xs mt-1 ml-1">{errors.amount}</p>}
              </div>

              <div>
                <label className="block text-xs font-medium text-white/60 mb-1.5 ml-1">Category</label>
                {preSelectedCategory ? (
                  <div className="w-full bg-white/5 border border-white/10 text-white/80 rounded-xl px-4 py-3 cursor-not-allowed flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: preSelectedCategory.color || '#6B7280' }} />
                    {preSelectedCategory.name}
                  </div>
                ) : (
                  <select
                    value={formData.categoryId}
                    onChange={e => setFormData({ ...formData, categoryId: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all appearance-none"
                  >
                    <option value="" className="bg-gray-900 text-white/60">Select a category</option>
                    {availableCategories.map(c => (
                      <option key={c.id} value={c.id} className="bg-gray-900 text-white">
                        {c.name} (₹{c.current_balance})
                      </option>
                    ))}
                  </select>
                )}
                {errors.categoryId && <p className="text-red-400 text-xs mt-1 ml-1">{errors.categoryId}</p>}
              </div>

              <div>
                <label className="block text-xs font-medium text-white/60 mb-1.5 ml-1">Receiver Details</label>
                <div className="space-y-3">
                  <div className="relative">
                    <input
                      placeholder="Enter UPI ID"
                      value={formData.upiId}
                      onChange={e => setFormData({ ...formData, upiId: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 text-white rounded-xl pl-4 pr-12 py-3 placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all"
                    />
                    <button
                      onClick={() => setShowScanner(true)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 hover:bg-white/10 rounded-lg text-primary-400 transition-colors"
                      title="Scan QR Code"
                    >
                      <Camera size={18} />
                    </button>
                  </div>
                  {errors.upiId && <p className="text-red-400 text-xs mt-1 ml-1">{errors.upiId}</p>}

                  <input
                    placeholder="Receiver Name (Optional)"
                    value={formData.merchantName}
                    onChange={e => setFormData({ ...formData, merchantName: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-3 placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all"
                  />
                </div>
              </div>

              <Button
                onClick={handlePayment}
                className="w-full shadow-lg shadow-primary-500/20 active:scale-[0.98]"
                size="lg"
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Copy & Continue'}
              </Button>

              <div className="flex items-center justify-center gap-2 text-xs text-white/40 mt-2">
                <Shield size={12} />
                SpendlyPay tracks budget, payments happen in UPI app
              </div>
            </div>
          )}

          {/* ================= APP CHOOSER ================= */}
          {step === STEPS.APP_CHOOSER && (
            <div className="p-6 space-y-6">
              <div className="text-center py-2">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-emerald-500/30">
                  <CheckCircle className="text-emerald-400" size={32} />
                </div>
                <h3 className="font-bold text-xl text-white">Details Copied!</h3>
                <p className="text-sm text-white/50 mt-1">
                  Paste details in your UPI app to pay
                </p>
                <div className="mt-4 p-3 bg-white/5 rounded-xl border border-white/10 inline-block">
                  <p className="text-emerald-400 font-bold text-lg">₹{formData.amount}</p>
                  <p className="text-xs text-white/40">{formData.upiId}</p>
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-xs font-medium text-white/40 uppercase tracking-wider ml-1">Open UPI App</p>
                {[
                  ['Google Pay', 'gpay', googlePayLogo],
                  ['PhonePe', 'phonepe', phonePeLogo],
                  ['Paytm', 'paytm', paytmLogo],
                  ['BHIM UPI', 'bhim', bhimLogo]
                ].map(([label, key, logo]) => (
                  <Button
                    key={key}
                    onClick={() => openUPIApp(key)}
                    variant="glass"
                    className="w-full justify-start gap-4 p-3 h-auto group"
                  >
                    <img
                      src={logo}
                      alt={label}
                      className="w-10 h-10 rounded-full object-cover ring-2 ring-white/10 group-hover:ring-white/20 transition-all"
                    />
                    <span className="font-semibold text-white">{label}</span>
                    <Send className="ml-auto w-4 h-4 text-white/20 group-hover:text-white/60" />
                  </Button>
                ))}

                <Button
                  onClick={() => openUPIApp('other')}
                  variant="glass"
                  className="w-full justify-start gap-4 p-3 h-auto group"
                >
                  <div className="w-10 h-10 rounded-full bg-white/10 text-white/60 flex items-center justify-center font-bold ring-2 ring-white/10 group-hover:ring-white/20 transition-all">
                    •••
                  </div>

                  <div className="flex flex-col items-start gap-0.5">
                    <span className="font-semibold text-white">Other UPI App</span>
                    <span className="text-xs text-white/40 font-normal">
                      Open manually
                    </span>
                  </div>
                  <Send className="ml-auto w-4 h-4 text-white/20 group-hover:text-white/60" />
                </Button>
              </div>
            </div>
          )}

          {/* ================= CONFIRMATION ================= */}
          {step === STEPS.CONFIRMATION && (
            <div className="p-8 text-center space-y-6">
              <div className="w-20 h-20 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-2 border border-primary-500/30 animate-pulse">
                <Shield className="text-primary-400" size={40} />
              </div>

              <div>
                <h3 className="font-bold text-xl text-white mb-2">Did you complete the payment?</h3>
                <p className="text-white/50 text-sm">
                  Confirming will record this transaction in your budget
                </p>
              </div>

              <div className="space-y-3 pt-2">
                <Button
                  onClick={() => handlePaymentConfirmation(true)}
                  className="w-full"
                  size="lg"
                >
                  Yes, Payment Successful
                </Button>

                <Button
                  onClick={() => handlePaymentConfirmation(false)}
                  variant="ghost"
                  className="w-full text-white/60 hover:text-white"
                  size="lg"
                >
                  No, Cancel
                </Button>
              </div>
            </div>
          )}

          {/* ================= SUMMARY ================= */}
          {step === STEPS.SUMMARY && (
            <div className="p-8 text-center space-y-6">
              <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-2 border border-emerald-500/30">
                <CheckCircle className="text-emerald-400" size={48} />
              </div>

              <div>
                <h3 className="font-bold text-2xl text-white mb-2">Payment Recorded!</h3>
                <div className="glass-card p-4 mt-4 inline-block w-full">
                  <p className="text-white/60 text-sm mb-1">Deducted from budget</p>
                  <p className="text-white font-bold text-lg">{paymentSummary.categoryName}</p>
                  <div className="w-full h-px bg-white/10 my-3" />
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-white/60">Amount</span>
                    <span className="text-white font-bold">₹{paymentSummary.amountSpent}</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => {
                  setStep(STEPS.FORM)
                  onClose()
                }}
                className="w-full"
                size="lg"
              >
                Close
              </Button>
            </div>
          )}

          {toast && <Toast {...toast} />}
        </div>
      </div>

      <QRScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleQRScan}
      />
    </>
  )
}

export default QuickPayment
